import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Pressable,
  Linking,
} from "react-native";
import {
  AntDesign,
  Feather,
  Ionicons,
  MaterialIcons,
} from "@expo/vector-icons";
import * as Clipboard from "@react-native-clipboard/clipboard";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
  FloatingFocusManager,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";

import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";
import Fonts from "../../Resources/Fonts";
import { useDevice } from "../_layout";

const FloatingExerciseLink = observer(
  ({
    exercise,
    onSave,
    children,
    triggerStyle,
    onClose,
    isOpen: externalIsOpen,
    onOpenChange,
  }) => {
    const { t } = useTranslation();
    const { isMobile } = useDevice();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [linkText, setLinkText] = useState(exercise?.link || "");
    const [isDeleteHovered, setIsDeleteHovered] = useState(false);
    const [isInputHovered, setIsInputHovered] = useState(false);
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const anchorRef = useRef(null);

    // Use external state if provided, otherwise use internal state
    const isOpen =
      externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const setIsOpen = onOpenChange || setInternalIsOpen;

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        setIsOpen(open);
        if (!open && onClose) {
          onClose();
        }
      },
      placement: "right-start",
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ["left-start", "top", "bottom", "right", "left"],
        }),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            // Apply size constraints directly to the floating element during positioning
            Object.assign(elements.floating.style, {
              maxWidth: `${Math.max(
                280,
                Math.min(320, availableWidth - 32)
              )}px`,
              maxHeight: `${Math.max(200, availableHeight - 32)}px`,
            });
          },
          padding: 16,
        }),
        shift({
          padding: 16,
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "dialog" });
    const { getFloatingProps } = useInteractions([dismiss, role]);

    // Anchor to external ref
    useEffect(() => {
      if (anchorRef?.current) {
        refs.setReference(anchorRef.current);
        // Set positioned to true once the reference is set
        setIsPositioned(true);
      }
    }, [anchorRef, refs]);

    // Reset positioning state when opening/closing
    useEffect(() => {
      if (!isOpen) {
        setIsPositioned(false);
      }
    }, [isOpen]);

    // Sync linkText with exercise.link
    useEffect(() => {
      setLinkText(exercise?.link || "");
      setErrorMessage(""); // Clear error when link changes
    }, [exercise?.link, isOpen]);

    const handleSaveLink = async (newValue) => {
      const trimmedValue = newValue.trim();

      // If empty, just save it (allow clearing the link)
      if (!trimmedValue) {
        if (exercise && exercise.setProperty) {
          exercise.setProperty("link", "");
        }
        setErrorMessage("");
        return;
      }

      try {
        // First check: Web-specific validation to reject obviously invalid inputs like "d"
        const isValidWebURL = (url) => {
          // Must have at least a dot (domain structure) or protocol
          if (!url.includes(".") && !url.includes("://")) {
            return false;
          }

          // Try to create a URL object for validation
          try {
            // If it doesn't start with protocol, try adding https://
            const testUrl =
              url.startsWith("http://") || url.startsWith("https://")
                ? url
                : `https://${url}`;

            const urlObj = new URL(testUrl);

            // Must have a valid hostname with at least one dot (for TLD)
            if (!urlObj.hostname || !urlObj.hostname.includes(".")) {
              return false;
            }

            // Hostname should be more than just a single character
            if (urlObj.hostname.length < 3) {
              return false;
            }

            return true;
          } catch {
            return false;
          }
        };

        // Second check: Mobile app schemes and special URLs
        const isMobileCompatibleURL = (url) => {
          // Common mobile app schemes that should be allowed
          const mobileSchemes = [
            "whatsapp:",
            "telegram:",
            "instagram:",
            "twitter:",
            "facebook:",
            "youtube:",
          ];

          return mobileSchemes.some((scheme) =>
            url.toLowerCase().startsWith(scheme)
          );
        };

        // Check web URL format first (rejects single chars like "d")
        const passesWebValidation = isValidWebURL(trimmedValue);

        // Check if it's a mobile app scheme
        const isMobileURL = isMobileCompatibleURL(trimmedValue);

        // If it doesn't pass basic web validation and isn't a mobile scheme, reject it
        if (!passesWebValidation && !isMobileURL) {
          setErrorMessage("invalidUrl-string");
          return;
        }

        // Final check with Linking.canOpenURL (this will be permissive on web but strict on mobile)
        const canOpen = await Linking.canOpenURL(trimmedValue);
        console.log("Can open URL:", canOpen, trimmedValue);

        // If Linking says it can't open it, reject it
        if (!canOpen) {
          setErrorMessage("invalidUrl-string");
          return;
        }

        // Clear error and save the link as-is
        setErrorMessage("");
        if (exercise && exercise.setProperty) {
          exercise.setProperty("link", trimmedValue);
        }
        if (onSave) {
          onSave(trimmedValue);
        }
      } catch (error) {
        setErrorMessage("Error: Invalid URL format");
        console.error("URL validation error:", error);
      }
    };

    const handleCopyLink = async () => {
      const trimmedLink = linkText.trim();
      if (trimmedLink) {
        try {
          // For web, use the native browser clipboard API
          if (typeof navigator !== "undefined" && navigator.clipboard) {
            await navigator.clipboard.writeText(trimmedLink);
          } else {
            // Fallback for older browsers or React Native
            await Clipboard.setStringAsync(trimmedLink);
          }
          // await Linking.openURL(trimmedLink);
        } catch (error) {
          console.error("Failed to copy link:", error);
        }
      }
    };

    const handleDeleteLink = () => {
      setLinkText("");
      setErrorMessage(""); // Clear error when deleting
      if (exercise) {
        exercise.setProperty("link", "");
      }
    };

    const renderFloatingContent = () => {
      return (
        <View>
          <View style={styles.header}>
            <Text style={styles.title}>{t("videoLink-string")}</Text>
            <TouchableOpacity
              onPress={() => {
                setIsOpen(false);
                if (onClose) {
                  onClose();
                }
              }}
              style={{
                paddingLeft: 4,
                paddingBottom: 4,
                opacity: isCloseHovered ? 0.6 : 1,
              }}
              onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
              onMouseLeave={() => setIsCloseHovered(false)}
            >
              <AntDesign name="close" size={18} color={Colors.grayColor} />
            </TouchableOpacity>
          </View>

          <View style={styles.inputContainer}>
            <View
              style={{
                flex: 1,
                borderWidth: 1,
                borderColor: isInputHovered
                  ? "rgba(0, 0, 0, 0.5)"
                  : Colors.borderColor,
                borderRadius: 12,
                padding: 8,
                fontSize: 14,
                flexDirection: "row",
                backgroundColor: Colors.whiteColor,
                height: 40,
              }}
              onMouseEnter={() => !isMobile && setIsInputHovered(true)}
              onMouseLeave={() => setIsInputHovered(false)}
            >
              <TextInput
                style={{
                  color: Colors.blackColor,
                  flex: 1,
                  height: "100%",
                  outlineStyle: "none",
                  border: "none",
                  outline: "none",
                }}
                value={linkText}
                onChangeText={(text) => {
                  setLinkText(text);
                  // Clear error when user starts typing
                  if (errorMessage) {
                    setErrorMessage("");
                  }
                }}
                placeholder="URL"
                placeholderTextColor={Colors.grayColor}
                multiline={false}
                autoCapitalize="none"
                keyboardType="url"
                autoCorrect={false}
                maxLength={400}
                autoFocus
              />
              <TouchableOpacity
                style={{
                  paddingLeft: 8,
                  alignSelf: "center",
                  opacity: !isButtonHovered || isMobile ? 1 : 0.6,
                }}
                onPress={async () =>
                  linkText.trim() === exercise.link
                    ? handleCopyLink()
                    : await handleSaveLink(linkText)
                }
                onMouseEnter={() => !isMobile && setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
              >
                {linkText.trim() === exercise.link ? (
                  <Feather name="copy" size={20} color={Colors.bubbleColor} />
                ) : (
                  <Text style={{ color: Colors.bubbleColor }}>
                    {t("save-string")}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>

          {errorMessage ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{t(`${errorMessage}`)}</Text>
            </View>
          ) : null}

          <TouchableOpacity
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              backgroundColor: "transparent",
              borderWidth: 1,
              borderColor: isDeleteHovered ? Colors.redColor : Colors.redColor,
              borderRadius: 12,
              padding: 8,
              height: 40,
            }}
            onPress={handleDeleteLink}
            onMouseEnter={() => !isMobile && setIsDeleteHovered(true)}
            onMouseLeave={() => setIsDeleteHovered(false)}
          >
            <Ionicons
              name={isDeleteHovered ? "trash" : "trash-outline"}
              size={18}
              color={isDeleteHovered ? Colors.redColor : Colors.redColor}
              style={{}}
            />
            <Text
              style={{
                color: isDeleteHovered ? Colors.redColor : Colors.redColor,
              }}
            >
              {t("delete-string")}
            </Text>
          </TouchableOpacity>
        </View>
      );
    };

    const handleTriggerPress = () => {
      if (isOpen) {
        // If already open, close it
        setIsOpen(false);
        if (onClose) {
          onClose();
        }
      } else {
        // If closed, open it
        // Ensure the reference is set before opening
        if (anchorRef?.current) {
          refs.setReference(anchorRef.current);
          setIsPositioned(true);
          setIsOpen(true);
        }
      }
    };

    return (
      <View>
        <TouchableOpacity
          ref={anchorRef}
          onPress={handleTriggerPress}
          style={triggerStyle}
          collapsable={false}
        >
          {children}
        </TouchableOpacity>

        {isOpen && isPositioned && (
          <FloatingPortal>
            <FloatingFocusManager context={context} modal={false}>
              <View
                ref={refs.setFloating}
                style={[
                  {
                    position: "fixed",
                    zIndex: 1000,
                    padding: 16,
                    borderRadius: 14,
                    backgroundColor: Colors.whiteColor,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                    borderWidth: 0.2,
                    borderColor: Colors.borderColor,
                    minWidth: 280,
                    // Remove static maxWidth and maxHeight - let size middleware handle this
                    overflow: "auto",
                  },
                  floatingStyles,
                ]}
                {...getFloatingProps()}
              >
                {renderFloatingContent()}
              </View>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  errorContainer: {
    marginBottom: 12,
  },
  errorText: {
    fontSize: 12,
    color: Colors.redColor,
    fontWeight: "500",
  },
});

export default FloatingExerciseLink;
